import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import TouchBackend from 'react-dnd-touch-backend';
import { DndProvider } from 'react-dnd';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { activitiesURL } from 'config';

import ActivityTemplates from 'activity-templates';
import {
  ActivityHelp,
  ActivityTeamHelp,
  GameFeedback,
  // Character,
  ParentAdvice,
  ActivityResults,
} from 'components/flink-play';
import {
  setCurrentActivity,
  openActivityHelp,
  closeActivityHelp,
  openActivityTeamHelp,
  closeActivityTeamHelp,
  setGameInitialized,
  showFeedback,
  addIdleTime,
} from 'actions/gameActions';
import { stopAudio } from 'actions/audioActions';
// import { mobileAndTabletCheck } from "utils";
import { filesURL } from 'config';
import IdleTracker from 'components/common/IdleTracker';
import { withGetStringAudio } from 'components/hocs';
import { isMobileAppRuntime } from 'utils';
import { getMobileAppPlatform } from 'utils/isMobileAppRuntime';
import { isReadingLocaleDebugEnabled, logReadingLocale } from 'utils/readingLocaleDiagnostics';
import {
  appendPlacementLocaleDiagnostic,
  normalizePlacementLanguageCode,
  selectMobilePlacementLanguageAddon,
} from 'mobile-modules/locale/placement-language-addon';

import './TemplateWrapper.scss';


const parseVersionType = (value = '') => {
  const versionType = String(value || '').trim();
  const separatorIndex = versionType.indexOf('_');

  if (separatorIndex <= 0 || separatorIndex === versionType.length - 1) {
    return { versionType: '', version: '', locale: '' };
  }

  return {
    versionType,
    version: versionType.slice(0, separatorIndex),
    locale: versionType.slice(separatorIndex + 1),
  };
};

const getVersionTypeFromLocale = (locale = '') => {
  const localeCode = String(locale || '').trim();

  return !localeCode || localeCode === 'en' ? '' : `immersion_${localeCode}`;
};

const isPlacementBridgeActivity = (desktopAssessmentBridge, currentActivity) => {
  if (!desktopAssessmentBridge?.enabled || desktopAssessmentBridge?.mode !== 'mobile') return false;
  const activityId = String(currentActivity?.activity?._id || currentActivity?._id || '');
  if (!activityId) return false;
  return (desktopAssessmentBridge.assessmentLeafActivityIds || [])
    .map(String)
    .includes(activityId);
};

// Avoid flashing a full-screen dialog for quick mobile activity transitions,
// including Placement. Keep the existing loader for genuinely slow requests.
const MOBILE_ACTIVITY_LOADING_DELAY_MS = 900;

const getPlacementInstructionLanguage = (desktopAssessmentBridge) => normalizePlacementLanguageCode(
  desktopAssessmentBridge?.attempt?.instructionLanguage
  || desktopAssessmentBridge?.attempt?.language
  || desktopAssessmentBridge?.attempt?.explanationLanguage
  || ''
);

class TemplateWrapper extends Component {
  state = {};

  templateWrapper = React.createRef();

  lastPlacementLocaleDiagnosticKey = '';

  writePlacementLocaleDiagnostic = () => {
    const {
      currentActivity,
      activityLocale,
      solutionLocale,
      desktopAssessmentBridge,
      placementLanguageActive,
      instructionLanguage,
    } = this.props;

    const placementActivity = isPlacementBridgeActivity(desktopAssessmentBridge, currentActivity);
    if (!isMobileAppRuntime() || !placementActivity || !placementLanguageActive || !currentActivity?.activity) return;

    const placementInstructionLanguage = getPlacementInstructionLanguage(desktopAssessmentBridge)
      || normalizePlacementLanguageCode(instructionLanguage);
    const runtimeActivityLocaleCode = placementInstructionLanguage
      || activityLocale?.code
      || solutionLocale?.code
      || 'en';
    const requestedVersionType = currentActivity.activity.monolingual
      ? ''
      : getVersionTypeFromLocale(runtimeActivityLocaleCode);
    const languageVersionData = currentActivity?.data?.languageVersionData || {};
    const availableLanguageVersions = Object.keys(languageVersionData);
    const hasRequestedVersion = requestedVersionType
      ? !!languageVersionData[requestedVersionType]
      : false;
    const selectedVersionType = hasRequestedVersion ? requestedVersionType : '';
    const selectedContentLocale = selectedVersionType
      ? parseVersionType(selectedVersionType).locale
      : (currentActivity.activity.monolingual
        ? (currentActivity.activity.locale || runtimeActivityLocaleCode || 'en')
        : 'en');

    const payload = {
      activityId: currentActivity.activity._id || null,
      activityName: currentActivity.activity.activityName
        || currentActivity.activity.formattedName
        || currentActivity.activity.name
        || null,
      template: currentActivity?.template?.alias || currentActivity?.template?.activityTemplateName || null,
      instructionLanguage: placementInstructionLanguage || null,
      solutionLocale: solutionLocale?.code || null,
      activityLocale: activityLocale?.code || null,
      requestedVersionType: requestedVersionType || null,
      hasRequestedVersion,
      selectedVersionType: selectedVersionType || null,
      selectedContentLocale,
      availableLanguageVersions,
      hasImmersionVm: !!languageVersionData.immersion_vm,
    };
    const key = JSON.stringify(payload);
    if (key === this.lastPlacementLocaleDiagnosticKey) return;
    this.lastPlacementLocaleDiagnosticKey = key;
    appendPlacementLocaleDiagnostic('activity-content', payload);
  };

  getActivityLoadingLabel = () => {
    const { activity, solutionMenu } = this.props;
    const activities = Array.isArray(solutionMenu?.activities) ? solutionMenu.activities : [];
    const activityDoc = activities.find((item) => String(item?._id) === String(activity)) || null;

    return [
      activityDoc?.activityName,
      activityDoc?.formattedName,
      activityDoc?.name,
      activityDoc?.title,
    ].filter(Boolean).join(' ');
  };

  setMobileActivityLoadingPhase = (detail) => {
    if (!isMobileAppRuntime() || typeof window === 'undefined') return;

    window.dispatchEvent(new CustomEvent('flc:mobile-loading-phase', {
      detail: {
        source: 'activity',
        ...detail,
      },
    }));
  };

  clearMobileActivityLoadingPhase = (immediate = false) => {
    if (this.mobileActivityLoadingTimer != null && typeof window !== 'undefined') {
      window.clearTimeout(this.mobileActivityLoadingTimer);
      this.mobileActivityLoadingTimer = null;
    }
    this.setMobileActivityLoadingPhase({
      active: false,
      immediate,
    });
  };

  calcTextStyles = () => {
    const { currentActivity } = this.props;

    this.setState({
      textStyle: currentActivity &&
        currentActivity.data &&
        currentActivity.data.options && {
          color: currentActivity.data.options.fontColor || '#000',
          fontSize: (currentActivity.data.options.fontSize || 18) / 11 + 'vmin',
          fontFamily:
            currentActivity.data.options.fontFamily || 'Roboto, sans-serif',
        },
    });
  };

  componentDidMount() {
    const {
      preview,
      team,
      activity,
      closeHandler,
      activityTemplates,
      setCurrentActivity,
    } = this.props;

    disableBodyScroll();

    if (isMobileAppRuntime()) {
      const viewport = document.querySelector('meta[name="viewport"]') || document.createElement('meta');
      viewport.setAttribute('name', 'viewport');
      viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');
      if (!viewport.parentNode) document.head.appendChild(viewport);
    }

    // mobileAndTabletCheck() &&
    //   document.body.addEventListener("click", this.requestFullscreen);

    if (isMobileAppRuntime()) {
      const activityLabel = this.getActivityLoadingLabel();
      const showLoading = () => {
        this.mobileActivityLoadingTimer = null;
        this.setMobileActivityLoadingPhase({
          active: true,
          label: 'Loading activity...',
          detail: activityLabel ? `Preparing ${activityLabel}` : 'Preparing activity content',
          progress: 18,
        });
      };
      if (!preview) {
        this.mobileActivityLoadingTimer = window.setTimeout(
          showLoading,
          MOBILE_ACTIVITY_LOADING_DELAY_MS
        );
      } else {
        showLoading();
      }
    }

    Promise.resolve(setCurrentActivity(activity, activityTemplates, {
      isPreview: preview,
      goBackHandler: closeHandler,
      players: team,
    })).catch((error) => {
      console.error('[mobile-activity][load-error]', error);
      this.clearMobileActivityLoadingPhase(true);
    });
  }

  requestFullscreen = () => {
    if (
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement
    ) {
      return;
    }

    const rootElem = document.getElementById('rootElem');

    if (rootElem.requestFullScreen) {
      rootElem.requestFullScreen();
    } else if (rootElem.mozRequestFullScreen) {
      rootElem.mozRequestFullScreen();
    } else if (rootElem.webkitRequestFullScreen) {
      rootElem.webkitRequestFullScreen();
    }
  };

  componentDidUpdate(prevProps) {
    const {
      isTeam,
      settings,
      currentActivity,
      initialActivityHelpShown,
      initialTeamActivityHelpShown,
      isActivityHelpOpened,
      isTeamActivityHelpOpened,
      history,
      closeHandler,
      gameInitialized,
      shouldGameInit,
    } = this.props;
    const { Template: TemplateInState } = this.state;

    this.writePlacementLocaleDiagnostic();

    if (currentActivity && !TemplateInState) {
      let Template;

      try {
        const TemplateData = ActivityTemplates[currentActivity.template.alias];

        if (!TemplateData) {
          throw new Error('template not found');
        }

        const isValidActivity = TemplateData.validateActivity(
          currentActivity.data
        );

        if (!isValidActivity) {
          throw new Error('Not enought data for activity');
        }

        if (TemplateData.Template) {
          Template = TemplateData.Template;
        } else {
          throw new Error('template not found');
        }
      } catch (e) {
        console.error(e.message);
      }

      if (!Template) {
        this.clearMobileActivityLoadingPhase(true);
        return closeHandler ? closeHandler() : history.goBack();
      }

      this.setState({ Template }, () => {
        this.calcTextStyles();

        // Keep the overlay until the activity template itself is committed, not
        // merely until the API promise resolves. This removes the blank interval
        // between tapping an activity and seeing its first real frame.
        if (isMobileAppRuntime()) {
          if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => this.clearMobileActivityLoadingPhase(true));
          } else {
            this.clearMobileActivityLoadingPhase(true);
          }
        }
      });
    }

    if (
      shouldGameInit &&
      !gameInitialized &&
      prevProps.shouldGameInit === false
    ) {
      this.startTemplate();
    }

    const showTeamHelp = false && isTeam && settings && settings.teamHelp;

    // If is team then show team activity help
    if (
      showTeamHelp &&
      !initialTeamActivityHelpShown &&
      !isTeamActivityHelpOpened
    ) {
      this.showInitialTeamActivityHelp();
    }

    // If is single learner then show activity help
    if (!showTeamHelp && !initialActivityHelpShown && !isActivityHelpOpened) {
      this.showInitialActivityHelp();
    }
  }

  showInitialTeamActivityHelp = () => {
    const {
      openActivityTeamHelp,
      closeActivityTeamHelp,
      settings,
      isTeam,
    } = this.props;

    if (!isTeam) return closeActivityTeamHelp();

    if (!settings || (settings && settings.teamHelp)) {
      openActivityTeamHelp();
    } else {
      closeActivityTeamHelp();
    }
  };

  showInitialActivityHelp = () => {
    const { openActivityHelp, closeActivityHelp, settings } = this.props;

    if (!settings || (settings && settings.activityHelp)) {
      openActivityHelp();
      // closeActivityHelp(); // for testing to disable help
    } else {
      closeActivityHelp();
    }
  };

  startTemplate = () => {
    document.dispatchEvent(new CustomEvent('startGame'));
    this.props.setGameInitialized();
  };

  componentWillUnmount() {
    const { setCurrentActivity } = this.props;

    this.clearMobileActivityLoadingPhase(true);

    // document.body.removeEventListener("click", this.requestFullscreen);
    // if (
    //   document.fullscreenElement ||
    //   document.webkitFullscreenElement ||
    //   document.mozFullScreenElement
    // ) {
    //   document.exitFullscreen();
    // }
    clearAllBodyScrollLocks();
    setCurrentActivity(null);
    stopAudio();
  }

  render() {
    const {
      translate,
      getStringAudio,
      preview,
      aspectRatio,
      addIdleTime,
      showFeedback,
      solutionLocale,
      activityLocale,
      locales,
      currentActivity,
      isActivityHelpOpened,
      isTeamActivityHelpOpened,
      multiplayerModeEnabled,
      versionType: explicitVersionType,
      isPlayVersion,
      desktopAssessmentBridge,
    } = this.props;
    const { Template, textStyle } = this.state;

    const activity = currentActivity?.activity ? currentActivity.activity : null;
    const searchParams = new URLSearchParams(window.location.search || '');
    const sharePreviewLocale = preview
      ? (searchParams.get('locale') || searchParams.get('lang') || '').trim()
      : '';
    const explicitVersion = parseVersionType(explicitVersionType);
    const isEditor = window.location.href.indexOf('edit-activities') !== -1;
    const placementActivity = isPlacementBridgeActivity(desktopAssessmentBridge, currentActivity);
    const placementLocaleCode = placementActivity
      ? getPlacementInstructionLanguage(desktopAssessmentBridge)
      : '';
    const placementTranslate = placementActivity ? translate : null;
    const runtimeActivityLocaleCode =
      placementLocaleCode || activityLocale?.code || solutionLocale?.code || 'en';
    const editorActivityLocaleCode = isEditor
      ? activity?.currentLanguageVersion?.locale
      : '';
    const requestedVersionType = activity?.monolingual ? '' : (
      explicitVersion.versionType
      || getVersionTypeFromLocale(sharePreviewLocale)
      || getVersionTypeFromLocale(editorActivityLocaleCode)
      || getVersionTypeFromLocale(runtimeActivityLocaleCode)
    );
    const hasLocalizedGameData = requestedVersionType
      ? !!currentActivity?.data?.languageVersionData?.[requestedVersionType]
      : false;
    const versionType = hasLocalizedGameData ? requestedVersionType : '';
    const activeVersion = parseVersionType(versionType);
    const contentLocaleCode = activeVersion.locale || (
      activity?.monolingual
        ? (activity?.locale || runtimeActivityLocaleCode || 'en')
        : 'en'
    );
    // In Flink Make the selected language version is passed through versionType
    // (for example immersion_es). Parent Advice in preview follows the selected
    // content version; regular Play resolves its default from the Solution UI locale.
    const parentAdviceLocaleCode =
      explicitVersion.locale
      || sharePreviewLocale
      || editorActivityLocaleCode
      || contentLocaleCode
      || runtimeActivityLocaleCode;
    const localeFromList = contentLocaleCode && Array.isArray(locales)
      ? locales.find(({ code }) => code === contentLocaleCode)
      : null;
    const effectiveSolutionLocale = contentLocaleCode
      ? (localeFromList || { ...(activityLocale || solutionLocale || {}), code: contentLocaleCode })
      : (activityLocale || solutionLocale);
    const activityForTemplate = currentActivity
      ? {
        ...currentActivity,
        activity: {
          ...currentActivity.activity,
          currentLanguageVersion: {
            version: activeVersion.version || '',
            locale: contentLocaleCode,
          },
        },
      }
      : currentActivity;

    
    // Read-only checkpoint before the template is mounted: Preview and Play
    // can select different language versions of the same saved Activity.
    if (isReadingLocaleDebugEnabled() && String(currentActivity?.template?.alias || '')
      .toUpperCase().replace(/-/g, '_') === 'READING_DELUXE') {
      logReadingLocale('template-wrapper', {
        activityId: activity?._id || null,
        preview: !!preview,
        isPlayVersion: !!isPlayVersion,
        activityLocale: activityLocale?.code || null,
        solutionLocale: solutionLocale?.code || null,
        effectiveSolutionLocale: effectiveSolutionLocale?.code || null,
        explicitVersionType: explicitVersionType || null,
        currentLanguageVersion: activity?.currentLanguageVersion || null,
        sharePreviewLocale: sharePreviewLocale || null,
        editorActivityLocale: editorActivityLocaleCode || null,
        requestedVersionType: requestedVersionType || '(base)',
        selectedVersionType: versionType || '(base)',
        hasLocalizedGameData,
        contentLocale: contentLocaleCode,
        availableVersions: Object.keys(currentActivity?.data?.languageVersionData || {}),
        gameDataPages: currentActivity?.data?.gameData?.problems?.length ?? null,
        selectedPages: versionType
          ? currentActivity?.data?.languageVersionData?.[versionType]?.problems?.length ?? null
          : currentActivity?.data?.gameData?.problems?.length ?? null,
      });
    }

    const backgroundImage = preview
      ? `url(${filesURL}/Themes/KidsPatterns/Backgrounds/background4.jpg)`
      : undefined;

    const styles = preview ? { backgroundImage } : { background: 'none' };
    const mobileLayout = isMobileAppRuntime();
    const mobilePlatform = mobileLayout ? getMobileAppPlatform() : 'web';
    const templateAlias = String(currentActivity?.template?.alias || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-');
    const wrapperClasses = [
      'template-wrapper',
      mobileLayout ? 'template-wrapper--mobile' : '',
      mobilePlatform === 'android' ? 'template-wrapper--android' : '',
      templateAlias ? `template-wrapper--${templateAlias}` : '',
    ].filter(Boolean).join(' ');
    const technicalTemplateName = String(
      currentActivity?.template?.alias
      || currentActivity?.template?.activityTemplateName
      || currentActivity?.activity?.templateId
      || 'UNKNOWN'
    ).trim();

    return (
      <DndProvider backend={TouchBackend} options={{ enableMouseEvents: true }}>
        <div
          className={wrapperClasses}
          data-activity-template={templateAlias || undefined}
          style={styles}
          ref={this.templateWrapper}
        >
          {mobileLayout && currentActivity && (
            <div className="activity-template-technical-label">
              TEMPLATE: {technicalTemplateName}
            </div>
          )}
          {currentActivity && Template && (
            <>
              <IdleTracker
                triggerHandler={addIdleTime}
                triggerMs={10 * 60 * 1000}
              />
              {isActivityHelpOpened && (
                <ActivityHelp
                  versionType={versionType}
                  locale={placementLocaleCode || undefined}
                  placementLocaleCode={placementLocaleCode}
                  placementTranslate={placementTranslate}
                />
              )}
              {isTeamActivityHelpOpened && <ActivityTeamHelp />}

              <ActivityResults
                desktopAssessmentBridge={desktopAssessmentBridge}
                placementTranslate={placementTranslate}
              />
              <GameFeedback
                versionType={versionType}
                placementLocaleCode={placementLocaleCode}
                placementTranslate={placementTranslate}
              />

              <Template
                contentFolder={versionType 
                  ? `${activitiesURL}/${currentActivity.activity._id}/languageVersionData/${versionType}/` 
                  : `${activitiesURL}/${currentActivity.activity._id}/gamedata/`}
                textStyle={textStyle || {}}
                solutionLocale={effectiveSolutionLocale}
                activity={activityForTemplate}
                versionType={versionType}
                preview={preview}
                translate={translate}
                getStringAudio={getStringAudio}
                aspectRatio={aspectRatio}
                showFeedback={showFeedback}
                multiplayerModeEnabled={multiplayerModeEnabled}
                isPlayVersion={isPlayVersion}
                mobileLayout={mobileLayout}
              />

              {preview && (
                <ParentAdvice contentLocaleCode={parentAdviceLocaleCode} />
              )}
              {/* {preview && <Character />} */}
            </>
          )}
        </div>
      </DndProvider>
    );
  }
}

TemplateWrapper.propTypes = {
  translate: PropTypes.func.isRequired,
  getStringAudio: PropTypes.func.isRequired,
  settings: PropTypes.object,
  solutionLocale: PropTypes.object,
  activityLocale: PropTypes.object,
  placementLanguageActive: PropTypes.bool,
  instructionLanguage: PropTypes.string,
  locales: PropTypes.array,
  openActivityHelp: PropTypes.func.isRequired,
  closeActivityHelp: PropTypes.func.isRequired,
  openActivityTeamHelp: PropTypes.func.isRequired,
  closeActivityTeamHelp: PropTypes.func.isRequired,
  addIdleTime: PropTypes.func.isRequired,
  isActivityHelpOpened: PropTypes.bool,
  isTeamActivityHelpOpened: PropTypes.bool,
  initialActivityHelpShown: PropTypes.bool,
  initialTeamActivityHelpShown: PropTypes.bool,
  aspectRatio: PropTypes.number,
  isTeam: PropTypes.bool,
  versionType: PropTypes.string,
  isPlayVersion: PropTypes.bool,
};

const mapStateToProps = (state, ownProps) => {
  const { auth, game, common, flinkPlay = {}, status } = state;
  const { learner, team, solutionTranslate, solutionLocale, activityLocale, solutionMenu } = flinkPlay;
  const placementLanguageAddon = selectMobilePlacementLanguageAddon(
    state,
    ownProps?.desktopAssessmentBridge?.attempt || null
  );

  return {
    isTeam: auth.isTeam,
    team: learner ? [learner, ...(team ?? [])] : [],
    multiplayerModeEnabled: game.multiplayerModeEnabled,
    solutionLocale: solutionLocale || status.lang,
    activityLocale: activityLocale || solutionLocale || status.lang,
    placementLanguageActive: placementLanguageAddon.active,
    instructionLanguage: placementLanguageAddon.active ? placementLanguageAddon.localeCode : '',
    currentActivity: game.currentActivity,
    initialActivityHelpShown: game.initialActivityHelpShown,
    initialTeamActivityHelpShown: game.initialTeamActivityHelpShown,
    isActivityHelpOpened: game.isActivityHelpOpened,
    isTeamActivityHelpOpened: game.isTeamActivityHelpOpened,
    shouldGameInit: game.shouldGameInit,
    gameInitialized: game.gameInitialized,
    activityTemplates: common.activityTemplates,
    solutionMenu,
    locales: common.locales,
    settings: learner && learner.settings,
    translate: placementLanguageAddon.active
      ? placementLanguageAddon.translate
      : (solutionTranslate || status.translate),
    aspectRatio: status.aspectRatio,
  };
};

export default compose(
  withRouter,
  withGetStringAudio,
  // DragDropContext(TouchBackend({ enableMouseEvents: true })),
  connect(mapStateToProps, {
    addIdleTime,
    setCurrentActivity,
    openActivityHelp,
    closeActivityHelp,
    openActivityTeamHelp,
    closeActivityTeamHelp,
    setGameInitialized,
    showFeedback,
  })
)(TemplateWrapper);
